/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, Mic, Sparkles, Square, Sunrise } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorText, PageHeader, Spinner, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';

const ST: Record<string, [string, 'slate' | 'green' | 'red' | 'amber']> = { PLANNED: ['ждёт подтверждения', 'amber'], DONE: ['выполнено', 'green'], FAILED: ['ошибка', 'red'], CANCELLED: ['отменено', 'slate'] };
const time = (s: string) => new Date(s).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });

/** Запись голоса в браузере и локальная расшифровка на сервере (звук никуда не передаётся) */
export function useDictation(onText: (t: string) => void) {
  const rec = useRef<MediaRecorder | null>(null);
  const [state, setState] = useState<'idle' | 'rec' | 'busy'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const start = async () => {
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: Blob[] = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState('busy');
        try {
          const fd = new FormData(); fd.append('file', new Blob(chunks, { type: r.mimeType }), 'voice.webm');
          const res = await api.post<{ text: string }>('/agent/voice', fd);
          if (res.text) onText(res.text); else setErr('Речь не распознана');
        } catch (e: any) { setErr(e.message); }
        setState('idle');
      };
      r.start(); rec.current = r; setState('rec');
    } catch { setErr('Нет доступа к микрофону'); }
  };
  const stop = () => rec.current?.state === 'recording' && rec.current.stop();
  return { state, err, start, stop };
}

export function MicButton({ onText }: { onText: (t: string) => void }) {
  const d = useDictation(onText);
  return (
    <span className="inline-flex items-center gap-2">
      {d.state === 'rec'
        ? <Button variant="danger" onClick={d.stop}><Square size={16} />Стоп</Button>
        : <Button variant="secondary" disabled={d.state === 'busy'} onClick={d.start}><Mic size={16} />{d.state === 'busy' ? 'Распознаю...' : 'Голосом'}</Button>}
      {d.err && <span className="text-xs text-red-600">{d.err}</span>}
    </span>
  );
}

/** M32 ИИ-агент: команда текстом или голосом, план действий, выполнение только после подтверждения */
export function AgentPage() {
  const [cmd, setCmd] = useState('');
  return (
    <>
      <PageHeader title="ИИ-агент" subtitle="Скажите или напишите, что сделать: агент составит план, выполнит после вашего подтверждения" />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <AgentBox cmd={cmd} setCmd={setCmd} />
          <Runs />
        </div>
        <DigestWidget />
      </div>
    </>
  );
}

export function AgentBox({ matterId, cmd, setCmd }: { matterId?: string; cmd: string; setCmd: (s: string) => void }) {
  const qc = useQueryClient();
  const plan = useMutation({ mutationFn: () => api.post<any>('/agent/plan', { command: cmd, matterId }), onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-runs'] }) });
  const confirm = useMutation({ mutationFn: (id: string) => api.post<any>(`/agent/runs/${id}/confirm`, {}), onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent-runs'] }); setCmd(''); plan.reset(); } });
  const cancel = useMutation({ mutationFn: (id: string) => api.post<any>(`/agent/runs/${id}/cancel`, {}), onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent-runs'] }); plan.reset(); } });
  const run = plan.data;
  return (
    <div className="card space-y-3 p-4">
      <Textarea rows={3} value={cmd} onChange={(e) => setCmd(e.target.value)} placeholder={matterId ? 'Например: поставь задачу подготовить отзыв к пятнице и добавь заметку о звонке клиента' : 'Например: напомни завтра в 10 позвонить в суд по делу А40-12345/2026'} />
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={cmd.trim().length < 3 || plan.isPending} onClick={() => plan.mutate()}><Sparkles size={16} />{plan.isPending ? 'Составляю план...' : 'Составить план'}</Button>
        <MicButton onText={(t) => setCmd(cmd ? `${cmd} ${t}` : t)} />
      </div>
      <ErrorText error={plan.error} />
      {run && <div className="rounded border border-slate-200 p-3">
        {run.result?.reply && <div className="mb-2 text-sm">{run.result.reply}</div>}
        {run.plan.length ? <>
          <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm">{run.plan.map((s: any, i: number) => <li key={i}><b className="font-medium">{s.label}:</b> {s.explain}</li>)}</ol>
          <div className="flex gap-2">
            <Button disabled={confirm.isPending} onClick={() => confirm.mutate(run.id)}>Выполнить</Button>
            <Button variant="secondary" onClick={() => cancel.mutate(run.id)}>Отменить</Button>
          </div>
        </> : <div className="text-sm text-slate-500">Нет действий для выполнения</div>}
        <ErrorText error={confirm.error} />
      </div>}
      {confirm.data && <RunResult run={confirm.data} />}
    </div>
  );
}

function RunResult({ run }: { run: any }) {
  return <ul className="space-y-1 text-sm">{(run.result?.steps ?? []).map((s: any, i: number) => (
    <li key={i} className="flex items-start gap-2">{s.ok ? <CheckCircle2 size={16} className="mt-0.5 text-green-700" /> : <CircleAlert size={16} className="mt-0.5 text-red-600" />}<span>{run.plan[i]?.explain}{!s.ok && <span className="block text-red-700">{s.error}</span>}</span></li>
  ))}</ul>;
}

function Runs() {
  const { data } = useQuery({ queryKey: ['agent-runs'], queryFn: () => api.get<any[]>('/agent/runs') });
  return (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium">История команд</div>
      <ul className="divide-y divide-slate-100 text-sm">
        {(data ?? []).map((r) => (
          <li key={r.id} className="py-2">
            <div className="flex items-center justify-between gap-2"><span>{r.command}</span><Badge tone={ST[r.status]?.[1]}>{ST[r.status]?.[0] ?? r.status}</Badge></div>
            <div className="text-xs text-slate-500">{time(r.createdAt)} · действий: {r.plan.length}</div>
            {r.status !== 'PLANNED' && r.result?.steps && <div className="mt-1"><RunResult run={r} /></div>}
          </li>
        ))}
        {!data?.length && <li className="py-2 text-slate-500">Команд пока не было</li>}
      </ul>
    </div>
  );
}

/** Утренняя сводка сотрудника (для страницы агента и главной) */
export function DigestWidget() {
  const [ai, setAi] = useState(false);
  const { data: d, isLoading, isFetching } = useQuery({ queryKey: ['agent-digest', ai], queryFn: () => api.get<any>(`/agent/digest${ai ? '?ai=1' : ''}`) });
  const Sec = ({ title, items, render }: { title: string; items: any[]; render: (x: any) => React.ReactNode }) => items?.length ? <div><div className="mt-3 text-xs font-semibold uppercase text-slate-500">{title} ({items.length})</div><ul className="mt-1 space-y-1 text-sm">{items.slice(0, 8).map((x, i) => <li key={i}>{render(x)}</li>)}</ul></div> : null;
  const ml = (id: string | null, t: string) => (id ? <Link className="text-brand-700 hover:underline" to={`/matters/${id}`}>{t}</Link> : t);
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium"><Sunrise size={16} />Сводка дня</div>
        <Button size="sm" variant="ghost" disabled={isFetching} onClick={() => setAi(true)}><Sparkles size={14} />Приоритеты</Button>
      </div>
      {isLoading ? <Spinner /> : d && <>
        {d.priorities && <div className="mt-2 whitespace-pre-wrap rounded bg-brand-50 p-2 text-sm">{d.priorities}</div>}
        <Sec title="Заседания" items={d.hearings} render={(h) => <>{time(h.next_hearing_at)} {ml(h.matter_id, h.number)}<span className="block text-xs text-slate-500">{h.court}{h.next_hearing_place ? `, ${h.next_hearing_place}` : ''}</span></>} />
        <Sec title="Сроки" items={d.deadlines} render={(x) => <>{fmtDate(x.due_date)}: {ml(x.matter_id, x.title)}</>} />
        <Sec title="Просрочено" items={d.overdue} render={(t) => <span className="text-red-700">{t.title}</span>} />
        <Sec title="На сегодня" items={d.today} render={(t) => t.title} />
        <Sec title="Новое в судах" items={d.courtEvents} render={(e) => <>{ml(e.matter_id, e.number)}: {e.title}</>} />
        <Sec title="Мониторинг" items={d.monitor} render={(e) => <>{e.name}: {e.title}</>} />
        {!d.hearings.length && !d.deadlines.length && !d.overdue.length && !d.today.length && !d.courtEvents.length && !d.monitor.length && <div className="mt-2 text-sm text-slate-500">Срочного ничего нет</div>}
      </>}
    </div>
  );
}

/** Проверка готовности дела к следующей стадии */
export function Readiness({ matterId }: { matterId: string }) {
  const [ai, setAi] = useState(false);
  const { data, isFetching } = useQuery({ queryKey: ['readiness', matterId, ai], queryFn: () => api.get<any>(`/agent/readiness/${matterId}${ai ? '?ai=1' : ''}`) });
  if (!data) return null;
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Готовность дела {data.ready ? <Badge tone="green">всё в порядке</Badge> : <Badge tone="amber">есть замечания</Badge>}</div>
        <Button size="sm" variant="ghost" disabled={isFetching} onClick={() => setAi(true)}><Sparkles size={14} />Проверить с ИИ</Button>
      </div>
      <ul className="space-y-1 text-sm">{data.checks.map((c: any, i: number) => <li key={i} className="flex items-start gap-2">{c.ok ? <CheckCircle2 size={16} className="mt-0.5 text-green-700" /> : <CircleAlert size={16} className="mt-0.5 text-amber-600" />}{c.text}</li>)}</ul>
      {data.advice && <div className="mt-2 whitespace-pre-wrap rounded bg-slate-50 p-2 text-sm">{data.advice}</div>}
    </div>
  );
}
